/**
 * Idle AI Endpoint Detector — small admin page that surfaces features
 * with configured budgets but zero traffic in the window.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Power, RefreshCcw } from 'lucide-react';
import BrandTitle from '../../components/BrandTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: 'bg-rose-500/15 text-rose-300',
  high: 'bg-amber-500/15 text-amber-300',
  medium: 'bg-yellow-500/15 text-yellow-300',
  low: 'bg-slate-500/15 text-slate-300',
};

export default function IdleEndpoints() {
  const [days, setDays] = useState(14);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/idle-endpoint/detect?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(await r.json());
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-5xl mx-auto px-6 py-8" data-testid="idle-endpoints-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex w-12 h-12 rounded-xl bg-amber-500 bg-gradient-to-br from-amber-500 to-orange-500 items-center justify-center shadow-lg shadow-amber-500/30">
            <Power className="w-6 h-6 text-white drop-shadow-sm" />
          </div>
          <div>
            <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-mono tracking-widest mb-1">WORLD-FIRST</Badge>
            <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Idle AI Endpoint Detector</BrandTitle></h1>
          </div>
        </div>
        <p className="text-base text-slate-700 max-w-3xl leading-relaxed">
          Features with configured token budgets but <span className="text-white">zero traffic in N days</span>. These are typically retired-but-not-cleaned-up deployments.
        </p>
      </div>

      <Card className="bg-slate-950 text-slate-100 border-amber-500/30 mb-6">
        <CardContent className="p-4 flex items-center gap-3">
          <span className="text-sm text-slate-400">Silent for at least</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="idle-window-select"
            className="bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
          <Button onClick={refresh} variant="ghost" className="text-cyan-300 ml-auto"><RefreshCcw className="w-4 h-4 mr-1" /> Refresh</Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />)}</div>
      ) : result && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6" data-testid="idle-summary-cards">
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">Idle features</div><div className="text-2xl font-bold text-white tabular-nums">{result.findings_count}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-amber-500/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-1">Idle Budget / mo</div><div className="text-2xl font-bold text-amber-300 tabular-nums">${result.total_idle_budget_usd}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Annual Recoverable</div><div className="text-2xl font-bold text-emerald-300 tabular-nums">${result.annual_recoverable_usd.toLocaleString()}</div></CardContent></Card>
          </div>

          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardHeader><CardTitle className="text-base text-white">Findings ({result.findings_count})</CardTitle></CardHeader>
            <CardContent>
              {result.findings.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400">No idle endpoints — every feature with a budget has fired in the last {days} days.</div>
              ) : (
                <div className="space-y-2">
                  {result.findings.map((f, i) => (
                    <div key={i} className="rounded-md border border-emerald-500/20 bg-slate-950 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-white">{f.feature_slug}</span>
                            <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${SEVERITY[f.severity]}`}>{f.severity}</span>
                          </div>
                          <div className="text-xs text-slate-400">{f.recommendation}</div>
                          {f.last_event_at && <div className="text-[10px] text-slate-500 mt-1">last event: {f.last_event_at}</div>}
                        </div>
                        <div className="text-right shrink-0 font-mono text-white">${f.budget_usd}/mo</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
      </div>
    </div>
  );
}
